import { FileText } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import { ClientPage } from '@/components/client/ClientPage';
import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { Pagination } from '@/components/ui/pagination';
import { StatusBadge } from '@/components/ui/status-badge';
import { INVOICE_TONES, useClientInvoices } from '@/hooks/invoices';
import { formatDay } from '@/lib/dates';
import { formatCents } from '@shared/money';
import { DEFAULT_PAGE_SIZE } from '@shared/schemas/pagination';

/*
 * /portal/invoices — ROADMAP.md §12.5's client view.
 *
 * Card list, not a table, at every width — the portal's own pattern
 * (FINDINGS.md §10.1), same as My Cleans. Drafts never appear here: the office's
 * workbench is not a bill, and the server refuses them rather than the screen
 * filtering them.
 */

export function ClientInvoicesPage() {
  const [page, setPage] = useState(1);
  const invoices = useClientInvoices(page);

  return (
    <ClientPage
      title="Invoices"
      description="Your invoices from us, newest first, with what has been paid and what is still owed."
    >
      {invoices.isPending ? (
        <div className="space-y-3">
          {[0, 1, 2].map((slot) => (
            <div key={slot} className="bg-cream-100 h-24 animate-pulse rounded-lg" />
          ))}
        </div>
      ) : invoices.error ? (
        <ErrorState
          title="Your invoices could not be loaded."
          onRetry={() => {
            void invoices.refetch();
          }}
        />
      ) : (invoices.data?.data.length ?? 0) === 0 ? (
        <EmptyState
          icon={FileText}
          title="No invoices yet."
          hint="When we send you one, it appears here with a PDF copy and how to pay."
        />
      ) : (
        <ul className="space-y-3">
          {(invoices.data?.data ?? []).map((row) => (
            <li key={row.id}>
              <Link
                to={`/portal/invoices/${row.id}`}
                className="block rounded-lg border border-ink-300 bg-white p-4 transition-colors hover:border-bark-700"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-label font-semibold text-ink-900">{row.invoiceNumber}</span>
                  <span className="flex items-center gap-2">
                    {row.isOverdue ? (
                      <span className="rounded-sm bg-red-100 px-1.5 py-0.5 text-meta font-medium text-red-800">
                        Overdue
                      </span>
                    ) : null}
                    <StatusBadge label={row.statusLabel} tone={INVOICE_TONES[row.status]} />
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-body text-ink-700">
                  <span>
                    {row.issueDate !== null ? `Issued ${formatDay(row.issueDate)}` : ''}
                    {row.dueDate !== null
                      ? ` · Due ${row.dueDate === row.issueDate ? 'on receipt' : formatDay(row.dueDate)}`
                      : ''}
                  </span>
                  <span className="tabular-nums">
                    {/* Zero is $0.00 — a settled balance is an answer (P1). */}
                    {formatCents(row.totalCents)}
                    {row.balanceCents > 0 ? (
                      <span className="ml-2 text-ink-500">
                        {formatCents(row.balanceCents)} owed
                      </span>
                    ) : row.status === 'paid' ? (
                      <span className="ml-2 text-ink-500">Paid in full</span>
                    ) : null}
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <Pagination
        page={page}
        pageSize={invoices.data?.pageSize ?? DEFAULT_PAGE_SIZE}
        total={invoices.data?.total ?? 0}
        onPageChange={setPage}
        itemNoun="invoices"
      />
    </ClientPage>
  );
}
